import { FunctionComponent, useEffect, useRef, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { Autocomplete, TextField } from "@mui/material";

import { CompanyLogoAvatar } from "@/components/CompanyLogoAvatar/CompanyLogoAvatar";
import { Information } from "@/components/Information/Information";
import { Icon } from "@/components/Icon/Icon";

export interface CompanyEnrichAutoCompleteItem {
	id: string;
	title: string;
	logoUrl?: string | null;
}

export interface CompanyEnrichAutoCompleteProps {
	handleSelect: (value?: CompanyEnrichAutoCompleteItem[]) => void;
	selectedItems: CompanyEnrichAutoCompleteItem[];
	fetchFn: (query: string) => Promise<CompanyEnrichAutoCompleteItem[]>;
	label?: string;
	testId?: string;
	information?: string | JSX.Element;
	placeholder?: string;
	disabled?: boolean;
	stayOn?: boolean;
	disabledItems?: string[];
	allowCustomValues?: boolean;
	/**
	 * When `allowCustomValues` is set, transforms the raw typed value before it
	 * becomes a selectable option (e.g. normalizing a pasted domain URL to its
	 * bare domain). Returning an empty string suppresses the custom option.
	 */
	normalizeCustomValue?: (value: string) => string;
}

export const CompanyEnrichAutoComplete: FunctionComponent<CompanyEnrichAutoCompleteProps> = ({
	handleSelect,
	selectedItems,
	fetchFn,
	label,
	information,
	placeholder,
	disabled = false,
	testId,
	stayOn = false,
	disabledItems = [],
	allowCustomValues = false,
	normalizeCustomValue,
}) => {
	const [options, setOptions] = useState<CompanyEnrichAutoCompleteItem[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [query, setQuery] = useState("");
	const searchTimer = useRef<NodeJS.Timeout | null>(null);

	useEffect(() => {
		if (searchTimer.current) {
			clearTimeout(searchTimer.current);
		}

		searchTimer.current = setTimeout(() => {
			setQuery(searchValue);
		}, 400);

		return () => {
			if (searchTimer.current) {
				clearTimeout(searchTimer.current);
			}
		};
	}, [searchValue]);

	useEffect(() => {
		if (!query) {
			if (!stayOn) {
				setOptions([]);
			}

			return;
		}

		setIsLoading(true);
		fetchFn(query)
			.then((results) => {
				setOptions(results);
			})
			.finally(() => {
				setIsLoading(false);
			});
	}, [query, fetchFn]);

	const rawCustomValue = searchValue.trim();
	const customValue =
		allowCustomValues && rawCustomValue.length > 0
			? (normalizeCustomValue?.(rawCustomValue) ?? rawCustomValue)
			: "";
	const optionsWithCustom =
		customValue.length > 0 && !options.some((option) => option.id === customValue)
			? [...options, { id: customValue, title: customValue }]
			: options;

	return (
		<div className="relative w-full" data-testid={testId}>
			{label && (
				<label className="flex flex-row items-center block mb-2 text-sm font-medium text-gray-900 appearance-none">
					{label}
					{information && (
						<span className="ml-1">
							<Information information={information} />
						</span>
					)}
				</label>
			)}

			<Autocomplete
				disableCloseOnSelect={stayOn}
				multiple
				clearOnBlur={true}
				disabled={disabled}
				// Options are already server-filtered (and include the normalized custom value);
				// skip MUI's client filter so a normalized option isn't hidden when the typed
				// text (e.g. a full URL) no longer matches its bare-domain label.
				filterOptions={(unfiltered) => unfiltered}
				getOptionDisabled={(option) => disabledItems.includes(option.id)}
				getOptionLabel={(option) => option.title}
				inputValue={searchValue || ""}
				isOptionEqualToValue={(option, currentValue) => {
					if (currentValue.title === "" && currentValue.id === "") {
						return true;
					}

					return option.id === currentValue.id;
				}}
				loading={isLoading}
				loadingText="Searching..."
				noOptionsText={
					searchValue && searchValue.length > 0 ? "Typing..." : "Start typing to search"
				}
				options={optionsWithCustom}
				popupIcon={<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />}
				renderOption={(props, option) => {
					const { key, ...liProps } = props as typeof props & { key?: string };

					return (
						<li key={key ?? option.id} {...liProps} className="flex items-center gap-3 py-2">
							<CompanyLogoAvatar logoUrl={option.logoUrl ?? null} name={option.title} size="sm" />
							<span className="truncate">{option.title}</span>
						</li>
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						label={placeholder || "Select an item"}
						placeholder={placeholder || "Select an item"}
						sx={{
							width: "100%",
							minHeight: 48,
							marginBottom: 1,
							"& .MuiFilledInput-root": {
								backgroundColor: "white",
								minHeight: 48,
								marginBottom: 0,
								borderRadius: 1.5,
								paddingTop: "10px",
								paddingBottom: "10px !important",
								paddingLeft: 1.5,
								"& .MuiChip-root": {
									backgroundColor: "#EAEEFF",
									color: "#1D3DF2",
								},
							},
							"& .MuiFilledInput-root:hover": {
								backgroundColor: "white",
							},
							"& .MuiInputLabel-root": {
								color: "#9CA3AF",
								fontSize: "16px",
								marginTop: "-4px",
							},
							"& .MuiInputLabel-root.MuiInputLabel-shrink": {
								display: "none",
							},
						}}
					/>
				)}
				size="small"
				sx={{
					width: "100%",
					marginBottom: 0,
					"& .MuiFormControl-root": {
						marginBottom: 0,
					},
				}}
				value={selectedItems || []}
				onBlur={(e) => {
					e.preventDefault();
					e.stopPropagation();
				}}
				onChange={(event, value) => {
					const filtered = value.filter((item) => !disabledItems.includes(item.id));

					handleSelect(filtered || []);
					setSearchValue("");
				}}
				onInputChange={(event, newValue, reason) => {
					if (reason === "input") {
						setSearchValue(newValue);
					}
				}}
			/>
		</div>
	);
};
